import {useEffect, useMemo, useRef, useState} from "react";
import {Box, Button, Dialog, DialogContent, IconButton, TextField, Typography} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import {
  COMBO_CATEGORY_ID,
  DRINK_CATEGORY_ID,
  SOUP_CATEGORY_ID,
  detailAmount,
  getComboDrinks,
  getCompositionSections,
  getFood,
  getLinkedSections,
  getMeal,
  pruneStaleLinkedDetail,
  itemComboDiscount,
  itemLineTotal,
  itemUnitPrice,
  sameId,
  updateOptionSelection,
  validateCustomization,
} from "../orderUtils";
import {useKioskLang, useKioskSession} from "../context/KioskSessionContext";
import {withTakeoutCup} from "../../utils/takeoutCup";

const newLineId = () => globalThis.crypto?.randomUUID?.() ?? `line-${Date.now()}-${Math.random()}`;

//選項左側的狀態指示：radio=圓點、checkbox=方框勾選
const OptionMark = ({type, selected}) => {
  if (type === "radio") {
    return <Box sx={{width: 22, height: 22, flex: "0 0 22px", borderRadius: "50%", border: "2px solid", borderColor: selected ? "primary.main" : "#C4B6A9", display: "grid", placeItems: "center", transition: "border-color .15s"}}>
      {selected && <Box sx={{width: 11, height: 11, borderRadius: "50%", bgcolor: "primary.main"}}/>}
    </Box>;
  }
  return <Box sx={{width: 22, height: 22, flex: "0 0 22px", borderRadius: "5px", border: "2px solid", borderColor: selected ? "primary.main" : "#C4B6A9", bgcolor: selected ? "primary.main" : "transparent", display: "grid", placeItems: "center", transition: "all .15s"}}>
    {selected && <CheckRoundedIcon sx={{fontSize: 17, color: "#FFF"}}/>}
  </Box>;
};

const sectionTypeHint = (tr, type) => type === "radio" ? tr("pickOne") : type === "selected" ? tr("adjustQty") : tr("multiSelect");

export default function ProductCustomizer({open, meal, item, systemData, onClose, onSave}) {
  const {tr, name} = useKioskLang();
  const {state: {orderMethod}} = useKioskSession();
  const [draft, setDraft] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    if (!open || !meal) return;
    setDraft(item ? {...item, detail: [...(item.detail ?? [])]} : {
      lineId: newLineId(), meal_id: meal.id, qty: 1, note: "", detail: withTakeoutCup(systemData, meal, [], orderMethod), combo_drink: null,
    });
    setShowErrors(false);
    // systemData 背景刷新時不重置草稿
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, meal, item, orderMethod]);

  const sections = useMemo(() => getCompositionSections(systemData, meal), [systemData, meal]);
  //細項連動類別（如選「套餐A」須再選「醬料」）：依目前已選內容動態長出
  const linkedSections = getLinkedSections(systemData, meal, draft?.detail);
  const hasCombo = (draft?.detail ?? []).some((detail) => sameId(getFood(systemData, detail.food_id)?.category_id, COMBO_CATEGORY_ID));
  const drinks = getComboDrinks(systemData);
  //套餐飲品／湯品分組顯示
  const drinkGroups = [
    {id: DRINK_CATEGORY_ID, label: "comboDrinkGroup"},
    {id: SOUP_CATEGORY_ID, label: "comboSoupGroup"},
  ].map((group) => ({...group, items: drinks.filter((drink) => sameId(drink.category_id, group.id))}))
    .filter((group) => group.items.length > 0);
  const selectedDrink = getMeal(systemData, draft?.combo_drink?.meal_id);
  const drinkSections = getCompositionSections(systemData, selectedDrink);
  const mainValidation = validateCustomization(systemData, meal, draft?.detail);
  const drinkValidation = hasCombo && selectedDrink ? validateCustomization(systemData, selectedDrink, draft?.combo_drink?.detail) : {valid: !hasCombo, missing: []};
  const valid = mainValidation.valid && (!hasCombo || (selectedDrink && drinkValidation.valid));
  const missingCount = mainValidation.missing.length + (hasCombo && !selectedDrink ? 1 : 0) + (hasCombo && selectedDrink ? drinkValidation.missing.length : 0);

  if (!draft || !meal) return null;

  //套餐優惠直接反映在價格上：顯示折後金額＋劃線原價（規則同後端 processOrder，見 orderUtils）
  const lineGross = itemUnitPrice(systemData, draft) * draft.qty;
  const comboDiscount = itemComboDiscount(systemData, draft);
  const lineTotal = itemLineTotal(systemData, draft);

  const chooseOption = (option, target = "main", operation = "toggle") => {
    if (Number(option.status) >= 2) return;
    if (target === "drink") {
      setDraft((current) => ({...current, combo_drink: {...current.combo_drink, detail: updateOptionSelection(systemData, current.combo_drink?.detail, option, operation)}}));
      return;
    }
    setDraft((current) => {
      //取消觸發細項時順手清掉殘留的連動選擇（如取消「套餐A」清掉醬料）
      const detail = pruneStaleLinkedDetail(systemData, meal, updateOptionSelection(systemData, current.detail, option, operation));
      const stillCombo = detail.some((selected) => sameId(getFood(systemData, selected.food_id)?.category_id, COMBO_CATEGORY_ID));
      return {...current, detail, combo_drink: stillCombo ? current.combo_drink : null};
    });
  };

  const selectDrink = (drink) => setDraft((current) => ({...current, combo_drink: {
    meal_id: drink.id, note: "", detail: sameId(current.combo_drink?.meal_id, drink.id) ? current.combo_drink.detail : withTakeoutCup(systemData, drink, [], orderMethod),
  }}));

  const save = () => {
    if (!valid) {
      setShowErrors(true);
      const first = mainValidation.missing[0]?.category_id ?? (hasCombo && !selectedDrink ? "drink" : drinkValidation.missing[0]?.category_id);
      sectionRefs.current[first]?.scrollIntoView({behavior: "smooth", block: "center"});
      return;
    }
    onSave(draft);
  };

  const renderSection = (section, target = "main") => {
    const detail = target === "drink" ? draft.combo_drink?.detail : draft.detail;
    const selectedCount = (detail ?? []).filter((selected) => sameId(getFood(systemData, selected.food_id)?.category_id, section.category_id)).reduce((sum, selected) => sum + Number(selected.qty), 0);
    const required = Number(section.menu?.required) === 1 || Number(section.min_select ?? section.menu?.min_select ?? 0) > 0;
    const missing = required && selectedCount === 0;
    const type = section.menu?.type ?? "checkbox";
    const isCounter = type === "selected";
    return (
      <Box key={`${target}-${section.category_id}`} ref={(node) => {sectionRefs.current[section.category_id] = node;}} sx={{p: 2, border: "2px solid", borderColor: showErrors && missing ? "error.main" : missing ? "rgba(179,54,42,.4)" : required ? "rgba(46,125,50,.4)" : "rgba(80,55,40,.14)", borderRadius: 1.5, bgcolor: missing ? "#FFFBF6" : "#FFF"}}>
        <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1.5}}>
          <Typography sx={{fontSize: 19, fontWeight: 900}}>{name(section.menu) || tr("chooseOptions")}</Typography>
          <Box sx={{px: 1.1, py: .3, borderRadius: "6px", fontSize: required ? 14.5 : 13.5, fontWeight: 900, bgcolor: required ? "#B3362A" : "#EFE8DF", color: required ? "#FFF" : "text.secondary"}}>{required ? tr("required") : tr("optional")}</Box>
          <Typography sx={{fontSize: 14.5, color: "text.secondary"}}>{sectionTypeHint(tr, type)}</Typography>
          {selectedCount > 0 && <Typography sx={{ml: "auto", fontSize: 14.5, fontWeight: 900, color: "success.main", display: "flex", alignItems: "center", gap: .3}}><CheckRoundedIcon sx={{fontSize: 17}}/>{tr("selectedCount", {n: selectedCount})}</Typography>}
          {showErrors && missing && <Typography sx={{ml: "auto", fontSize: 14.5, fontWeight: 900, color: "error.main"}}>{tr("pleaseChoose")}</Typography>}
          {!showErrors && missing && <Typography sx={{ml: "auto", fontSize: 14.5, fontWeight: 900, color: "#B3362A"}}>{tr("requiredHint")}</Typography>}
        </Box>
        <Box sx={{display: "grid", gridTemplateColumns: isCounter ? "1fr" : "repeat(auto-fill, minmax(170px, 1fr))", gap: 1}}>
          {section.options.map((option) => {
            const selected = (detail ?? []).find((entry) => sameId(entry.food_id, option.pd_id));
            const price = Number(option.price) > 0 ? `+$${Number(option.price).toLocaleString()}` : "";
            if (isCounter) {
              return (
                <Box key={option.pd_id} sx={{minHeight: 52, px: 1.5, py: .5, display: "flex", alignItems: "center", gap: 1, border: "1.5px solid", borderColor: selected ? "primary.main" : "rgba(80,55,40,.14)", bgcolor: selected ? "#FFF6F1" : "#FFF", borderRadius: 1, transition: "all .15s"}}>
                  <Typography sx={{fontSize: 17, fontWeight: 800, flex: 1, minWidth: 0}}>{name(option)}{detailAmount(option) && <Box component="span" sx={{ml: .75, fontSize: 14, fontWeight: 600, color: "text.secondary"}}>{detailAmount(option)}</Box>}</Typography>
                  {price && <Typography sx={{fontSize: 14.5, color: "text.secondary"}}>{price}</Typography>}
                  <IconButton aria-label={`${tr("decreaseQty")} ${name(option)}`} disabled={!selected} onClick={() => chooseOption(option, target, "remove")} sx={{width: 40, height: 40, border: "1px solid #D8CEC5", borderRadius: 1}}><RemoveRoundedIcon sx={{fontSize: 20}}/></IconButton>
                  <Typography sx={{width: 26, textAlign: "center", fontSize: 17, fontWeight: 900}}>{selected?.qty ?? 0}</Typography>
                  <IconButton aria-label={`${tr("increaseQty")} ${name(option)}`} onClick={() => chooseOption(option, target, "add")} sx={{width: 40, height: 40, border: "1px solid #D8CEC5", borderRadius: 1, color: "primary.main"}}><AddRoundedIcon sx={{fontSize: 20}}/></IconButton>
                </Box>
              );
            }
            return (
              <Box key={option.pd_id} onClick={() => chooseOption(option, target)} sx={{minHeight: 52, px: 1.25, py: .75, display: "flex", alignItems: "center", gap: 1, border: "1.5px solid", borderColor: selected ? "primary.main" : "rgba(80,55,40,.14)", bgcolor: selected ? "#FFF6F1" : "#FFF", borderRadius: 1, cursor: "pointer", transition: "all .15s", "&:active": {transform: "scale(.98)"}}}>
                <OptionMark type={type} selected={Boolean(selected)}/>
                <Typography sx={{fontSize: 17, fontWeight: 800, flex: 1, minWidth: 0, lineHeight: 1.25}}>{name(option)}{detailAmount(option) && <Box component="span" sx={{ml: .75, fontSize: 14, fontWeight: 600, color: "text.secondary"}}>{detailAmount(option)}</Box>}</Typography>
                {price && <Typography sx={{fontSize: 14.5, fontWeight: 800, color: selected ? "primary.main" : "text.secondary"}}>{price}</Typography>}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogContent sx={{p: 0, display: "flex", flexDirection: "column", bgcolor: "#F7F1E8"}}>
        <Box sx={{height: 72, flex: "0 0 72px", px: 2.5, bgcolor: "#FFF", display: "flex", alignItems: "center", gap: 1.5, borderBottom: "1px solid rgba(80,55,40,.1)"}}>
          <IconButton onClick={onClose} sx={{width: 44, height: 44, border: "1px solid rgba(80,55,40,.15)", borderRadius: 1}}><CloseRoundedIcon/></IconButton>
          <Typography sx={{fontSize: 23, fontWeight: 950, flex: 1, minWidth: 0}} noWrap>{name(meal)}</Typography>
          {comboDiscount > 0 && <Box sx={{px: 1.1, py: .3, borderRadius: "6px", fontSize: 14.5, fontWeight: 900, bgcolor: "#EFF7EE", color: "#2E7D32", border: "1px solid #BBD8B9"}}>{tr("comboDiscountTag")} -${comboDiscount.toLocaleString()}</Box>}
          {comboDiscount > 0 && <Typography sx={{fontSize: 17, fontWeight: 800, color: "text.secondary", textDecoration: "line-through"}}>${lineGross.toLocaleString()}</Typography>}
          <Typography sx={{fontSize: 24, fontWeight: 950, color: "primary.main"}}>${lineTotal.toLocaleString()}</Typography>
        </Box>
        <Box sx={{flex: 1, overflow: "auto", p: 2.5}}>
          <Box sx={{maxWidth: 1000, mx: "auto", display: "grid", gap: 1.5}}>
            {meal.description && <Typography sx={{fontSize: 17, color: "text.secondary"}}>{meal.description}</Typography>}
            {sections.length ? sections.map((section) => renderSection(section)) : <Typography sx={{fontSize: 18, color: "text.secondary"}}>{tr("noOptions")}</Typography>}
            {linkedSections.map((section) => renderSection(section))}
            {hasCombo && <Box ref={(node) => {sectionRefs.current.drink = node;}} sx={{p: 2, border: "2px solid", borderColor: showErrors && !selectedDrink ? "error.main" : !selectedDrink ? "rgba(179,54,42,.4)" : "rgba(46,125,50,.4)", borderRadius: 1.5, bgcolor: selectedDrink ? "#FFF" : "#FFFBF6"}}>
              <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1.5}}>
                <Typography sx={{fontSize: 19, fontWeight: 900}}>{tr("comboDrink")}</Typography>
                <Box sx={{px: 1.1, py: .3, borderRadius: "6px", fontSize: 14.5, fontWeight: 900, bgcolor: "#B3362A", color: "#FFF"}}>{tr("required")}</Box>
                {selectedDrink && <Typography sx={{ml: "auto", fontSize: 14.5, fontWeight: 900, color: "success.main", display: "flex", alignItems: "center", gap: .3}}><CheckRoundedIcon sx={{fontSize: 17}}/>{name(selectedDrink)}</Typography>}
                {!selectedDrink && <Typography sx={{ml: "auto", fontSize: 14.5, fontWeight: 900, color: showErrors ? "error.main" : "#B3362A"}}>{showErrors ? tr("pleaseChoose") : tr("requiredHint")}</Typography>}
              </Box>
              {drinkGroups.map((group) => <Box key={group.id} sx={{"& + &": {mt: 1.5}}}>
              {drinkGroups.length > 1 && <Typography sx={{fontSize: 15.5, fontWeight: 900, color: "text.secondary", mb: .75}}>{tr(group.label)}</Typography>}
              <Box sx={{display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 1}}>
                {group.items.map((drink) => {
                  const selected = sameId(selectedDrink?.id, drink.id);
                  return <Box key={drink.id} onClick={() => selectDrink(drink)} sx={{minHeight: 52, px: 1.25, py: .75, display: "flex", alignItems: "center", gap: 1, border: "1.5px solid", borderColor: selected ? "primary.main" : "rgba(80,55,40,.14)", bgcolor: selected ? "#FFF6F1" : "#FFF", borderRadius: 1, cursor: "pointer", transition: "all .15s", "&:active": {transform: "scale(.98)"}}}>
                    <OptionMark type="radio" selected={selected}/>
                    <Typography sx={{fontSize: 17, fontWeight: 800, flex: 1, minWidth: 0, lineHeight: 1.25}}>{name(drink)}</Typography>
                    {Number(drink.price) > 0 && <Typography sx={{fontSize: 14.5, fontWeight: 800, color: selected ? "primary.main" : "text.secondary"}}>+${drink.price}</Typography>}
                  </Box>;
                })}
              </Box>
              </Box>)}
            </Box>}
            {hasCombo && selectedDrink && drinkSections.map((section) => renderSection(section, "drink"))}
            <Box sx={{p: 2, border: "1.5px solid rgba(80,55,40,.14)", borderRadius: 1.5, bgcolor: "#FFF"}}>
              <Typography sx={{fontSize: 19, fontWeight: 900, mb: 1.5}}>{tr("qtyAndNote")}</Typography>
              <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                <IconButton aria-label={tr("decreaseQty")} disabled={draft.qty <= 1} onClick={() => setDraft((current) => ({...current, qty: Math.max(1, current.qty - 1)}))} sx={{width: 44, height: 44, border: "1px solid #D8CEC5", borderRadius: 1}}><RemoveRoundedIcon/></IconButton>
                <Typography sx={{fontSize: 23, fontWeight: 900, minWidth: 36, textAlign: "center"}}>{draft.qty}</Typography>
                <IconButton aria-label={tr("increaseQty")} onClick={() => setDraft((current) => ({...current, qty: Math.min(20, current.qty + 1)}))} sx={{width: 44, height: 44, border: "1px solid #D8CEC5", borderRadius: 1, color: "primary.main"}}><AddRoundedIcon/></IconButton>
                <TextField size="small" label={tr("itemNote")} value={draft.note} onChange={(event) => setDraft((current) => ({...current, note: event.target.value.slice(0, 100)}))} sx={{ml: 1.5, flex: 1}}/>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{px: 2.5, py: 1.75, bgcolor: "#FFF", borderTop: "1px solid rgba(80,55,40,.12)", display: "flex", alignItems: "center", gap: 1.5, pb: "calc(14px + env(safe-area-inset-bottom, 0px))"}}>
          {!valid && <Typography sx={{fontSize: 15.5, fontWeight: 900, color: showErrors ? "error.main" : "#B3362A"}}>{tr("missingRequired", {n: missingCount})}</Typography>}
          <Button variant="outlined" onClick={onClose} sx={{ml: "auto", minWidth: 120, minHeight: 56}}>{tr("cancel")}</Button>
          {/* 必選未完成時按鈕灰色（仍可點，點了捲動到缺漏區塊）；完成後亮起＋脈動提示 */}
          <Button variant="contained" onClick={save} sx={{minWidth: 250, minHeight: 56, fontSize: 19, animation: valid ? "kioskPulse 2s infinite" : "none", ...(!valid && {bgcolor: "#CDBFB2", color: "#FFF", boxShadow: "none", "&:hover": {bgcolor: "#C3B3A4"}})}}>{item ? tr("saveChanges") : tr("addToCart")} · ${lineTotal.toLocaleString()}</Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
